/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ActionType, CancelOnePassActionKernelV2 } from 'client-code/actions';
import { OnePassValues, OptionType } from 'client-code/onepass';

import { MAX_STORAGE_LIMIT } from '../../../config';
import { InteractiveComponents, SeparatorTypes } from '../../../core/enums';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import { TOverlayOptions } from '../../../core/types';
import { UIOrchestrator, UIOrchestratorState } from '../../../core/uiorchestrator';
import { DynamicOptionsEngine, UniqueIdentifier } from '../../../core/utils';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import {
    hideOverlayDialog,
    onKeyLeftOverlayDialog,
    onKeyRightOverlayDialog,
    setOverlayDialogOptions,
    showOverlayDialog,
} from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import {
    setOverlayHighlightHorizontalPositionAction,
    setOverlayHighlightVerticalPositionAction,
} from '../overlayhighlight/overlayhighlight.actions';
import { showStorageLimitOverlay } from '../storagelimitoverlay/storagelimitoverlay.reducers';

const GLOBAL_MOUSE_TRAP: string = 'HideOnePassOptionsOverOverlay';

// eslint-disable-next-line no-shadow
export enum OnePassOptionsOverlayType {
    CREATE,
    MODIFY,
}

// eslint-disable-next-line no-shadow
enum OnePassOptionsOverlayActions {
    CREATE_ONE_PASS,
    DELETE_ONE_PASS,
    EXIT_ONE_PASS_OVERLAY,
    UPDATE_ONE_PASS,
}

// deleteOnePassConfirmation
// eslint-disable-next-line no-shadow
enum DOCOverlayActions {
    NO,
    YES,
}

const TAG: string = 'OnePassOptionsOverlay';

/**
 * helper function, not to be exported
 */
const checkStorageAndPerformOnePassAction =
    (
        options: TOverlayOptions[],
        horizontalPosition: Array<number>,
        onepassType: OnePassOptionsOverlayType,
    ) =>
    (dispatch: Function) => {
        try {
            const diskSpacePercentageUsed = new SharedPreferences(
                SharedPrefConstants.DISK_SPACE_PREF_KEY,
            ).getNumber(SharedPrefConstants.DISK_SPACE_PERCENTAGE_USED);
            const userSelectedOnePassOptions = getUserSelectedOptions(options, horizontalPosition);
            const onePassSharedPreferenceEditor = new SharedPreferences(
                SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY,
            ).edit();
            onePassSharedPreferenceEditor.putJson(
                SharedPrefConstants.ONE_PASS_USER_SELECTED_OPTIONS,
                userSelectedOnePassOptions as unknown as JSON,
            );
            onePassSharedPreferenceEditor.commit();
            if (diskSpacePercentageUsed > MAX_STORAGE_LIMIT) {
                if (onepassType === OnePassOptionsOverlayType.CREATE)
                    dispatch(showStorageLimitOverlay(scheduleOnePass));
                else dispatch(showStorageLimitOverlay(modifyOnePass));
                return;
            }
            if (onepassType === OnePassOptionsOverlayType.CREATE) {
                dispatch(scheduleOnePass());
            } else {
                dispatch(modifyOnePass());
            }
        } catch (error) {
            dispatch(hideOverlayDialog());
            clearOnePassSharedPreference();
            Log.e(TAG, 'checkStorageAndPerformOnePassAction', error);
            Diagnostics.error(TAG, 'checkStorageAndPerformOnePassAction', error);
            dispatch(
                showWhisper({
                    message: Locale.getString(LocaleKeys.ONEPASS_ERROR_WHISPER_MESSAGE),
                    iconType: '',
                }),
            );
        }
    };

const deleteOnepass = () => async (dispatch: Function) => {
    const sharedPreferences = new SharedPreferences(SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY);
    const unifiedCollectionId = sharedPreferences.getString(
        SharedPrefConstants.SELECTED_ONE_PASS_UCID,
    );
    try {
        dispatch(hideOverlayDialog());
        await new ServicesClientImpl().deleteOnePass(unifiedCollectionId);
        UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.ONE_PASS_DELETED);
        dispatch(
            showWhisper({
                message: Locale.getString(LocaleKeys.ONEPASS_DELETED_WHISPER_MESSAGE),
                iconType: '',
            }),
        );
    } catch (error) {
        dispatch(
            showWhisper({
                message: Locale.getString(LocaleKeys.ONEPASS_ERROR_WHISPER_MESSAGE),
                iconType: '',
            }),
        );
    } finally {
        clearOnePassSharedPreference();
    }
};

const getUserSelectedOptions = (
    options: TOverlayOptions[],
    horizontalPosition: Array<number>,
): { unifiedCollectionId: string; userSelectedValues: OnePassValues } => {
    const sharedPreferences = new SharedPreferences(SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY);

    const dynamicOptionsGenerator = new DynamicOptionsEngine();
    const userSelectedValues = dynamicOptionsGenerator.getUserSelectedOnePassValues(
        options,
        horizontalPosition,
    );

    const unifiedCollectionId = sharedPreferences.getString(
        SharedPrefConstants.SELECTED_ONE_PASS_UCID,
    ) as any;

    return {
        unifiedCollectionId,
        userSelectedValues,
    };
};

const modifyOnePass = () => async (dispatch: Function) => {
    try {
        const userSelectedOptions: {
            unifiedCollectionId: string;
            userSelectedValues: OnePassValues;
        } = new SharedPreferences(SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY).getJson(
            SharedPrefConstants.ONE_PASS_USER_SELECTED_OPTIONS,
        ) as any;
        dispatch(hideOverlayDialog());
        const services = new ServicesClientImpl();
        await services.updateOnePass(
            userSelectedOptions.unifiedCollectionId,
            userSelectedOptions.userSelectedValues,
        );
        UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.ONE_PASS_MODIFIED);
        dispatch(
            showWhisper({
                message: Locale.getString(LocaleKeys.ONEPASS_MODIFIED_WHISPER_MESSAGE),
                iconType: '',
            }),
        );
    } catch (error) {
        Log.e(TAG, 'modifyOnepass', error);
        dispatch(
            showWhisper({
                message: Locale.getString(LocaleKeys.ONEPASS_ERROR_WHISPER_MESSAGE),
                iconType: '',
            }),
        );
    } finally {
        clearOnePassSharedPreference();
    }
};

// prepareDeleteOnepassConfirmationOverlay
export const prepareDOCOverlay = () => async (dispatch: Function) => {
    const overlayProps: IOverlayDialog = {
        enableCaret: true,
        hasDimmer: true,
        options: [
            {
                text: Locale.getString(LocaleKeys.CANCEL_ONEPASS_OVERLAY_OPTION_YES),
                hrTop: true,
                separatorType: SeparatorTypes.OPTIONS,
                value: DOCOverlayActions.YES,
            },
            {
                text: Locale.getString(LocaleKeys.CANCEL_ONEPASS_OVERLAY_OPTION_NO),
                value: DOCOverlayActions.NO,
            },
        ],
        mouseTrapValue: GLOBAL_MOUSE_TRAP,
        title: Locale.getString(LocaleKeys.CANCEL_ONEPASS_OVERLAY_TITLE),
        description: Locale.getString(LocaleKeys.CANCEL_ONEPASS_OVERLAY_DESCRIPTION),
        hasSeperator: false,
        initialVerticalIndex: 1,
    };
    dispatch(hideOverlayDialog());
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS));
    dispatch(showOverlayDialog(overlayProps));
};

export const prepareCancelOnePassOverlay = () => (dispatch: Function) => {
    const sharedPreferences = new SharedPreferences(SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY);
    const onePassAction = sharedPreferences.getJson(
        SharedPrefConstants.CANCEL_ONE_PASS_ACTION,
    ) as any;
    const onePassSharedPreferenceEditor = new SharedPreferences(
        SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY,
    ).edit();
    if (onePassAction.actionType === ActionType.MultiOnePassAction) {
        dispatch(showCancelOnePassOverlay(onePassAction.kernel.actions.actions));
    } else {
        onePassSharedPreferenceEditor.putString(
            SharedPrefConstants.SELECTED_ONE_PASS_UCID,
            onePassAction.kernel.unifiedCollectionId,
        );
        onePassSharedPreferenceEditor.apply();
        dispatch(prepareDOCOverlay());
    }
};

export const showCancelOnePassOverlay = (actions: any) => (dispatch: Function) => {
    const overlayOptions: TOverlayOptions[] = actions?.map((action): TOverlayOptions => {
        return {
            text: `${action.title}${action.postfixTitle ? ` (${action.postfixTitle})` : ''}`,
            value: (action.kernel as CancelOnePassActionKernelV2).unifiedCollectionId,
        };
    });
    const overlayProps: IOverlayDialog = {
        hasDimmer: true,
        hasSeperator: false,
        mouseTrapValue: GLOBAL_MOUSE_TRAP,
        options: overlayOptions,
        title: Locale.getString(LocaleKeys.CANCEL_ONEPASS_OVERLAY_TITLE),
    };
    dispatch(
        setScreenTaskController(ScreenTaskControllerTypes.MULTI_CANCEL_ONE_PASS_OVERLAY_TASKS),
    );
    dispatch(showOverlayDialog(overlayProps));
};

const computeNewOptions = (
    options: TOverlayOptions[],
    verticalPosition: number,
    horizontalPositions: number[],
    newHorizontalPosition: number,
) => {
    const currentHighLightedOption: TOverlayOptions = options[verticalPosition];
    const nextLabel = currentHighLightedOption.horizontalSelectionoptions![newHorizontalPosition];
    const optionsWithSelectedLabel: Map<OptionType, any> = new Map();
    const newOptions: TOverlayOptions[] = [];
    for (let i = 0; i < options.length; i += 1) {
        const option = options[i];
        if (!option.horizontalSelectionoptions) {
            newOptions.push(option);
        } else
            optionsWithSelectedLabel.set(
                option.value.optionListType,
                option.horizontalSelectionoptions[horizontalPositions[i]],
            );
    }
    optionsWithSelectedLabel.set(currentHighLightedOption.value.optionListType, nextLabel);
    const newComputedOptions = new DynamicOptionsEngine().computeOnePassOptions(
        optionsWithSelectedLabel,
    );
    newOptions.push(...newComputedOptions);
    const updatedHorizontalPositions = [...horizontalPositions];
    newOptions.forEach((option, index) => {
        if (Number(option?.initialHorizontalIndex) >= 0) {
            updatedHorizontalPositions[index] = option.initialHorizontalIndex!;
        }
    });
    return { updatedHorizontalPositions, newOptions };
};

const scheduleOnePass = () => async (dispatch: Function) => {
    try {
        dispatch(hideOverlayDialog());
        const userSelectedOptions: {
            unifiedCollectionId: string;
            userSelectedValues: OnePassValues;
        } = new SharedPreferences(SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY).getJson(
            SharedPrefConstants.ONE_PASS_USER_SELECTED_OPTIONS,
        ) as any;
        const services = new ServicesClientImpl();
        await services.createOnePass(
            userSelectedOptions.unifiedCollectionId,
            userSelectedOptions.userSelectedValues,
        );
        UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.ONE_PASS_CREATED);
        dispatch(
            showWhisper({
                message: Locale.getString(LocaleKeys.ONEPASS_SCHEDULED_WHISPER_MESSAGE),
                iconType: 'onepass-success',
            }),
        );
    } catch (error) {
        Log.e(TAG, 'scheduleOnePass', error);
        dispatch(
            showWhisper({
                message: Locale.getString(LocaleKeys.ONEPASS_ERROR_WHISPER_MESSAGE),
                iconType: '',
            }),
        );
    } finally {
        clearOnePassSharedPreference();
    }
};

/**
 * exportable helper functions
 */
export const clearOnePassSharedPreference = () => {
    const onePassSharedPreferenceEditor = new SharedPreferences(
        SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY,
    ).edit();
    onePassSharedPreferenceEditor.clear();
    onePassSharedPreferenceEditor.commit();
};

/**
 * show hide action creators
 */
export const showOnePassOptionsOverlay =
    (
        type: OnePassOptionsOverlayType,
        subtitle: string,
        unifiedCollectionId: string,
        channelId?: string,
        contentId?: string,
    ) =>
    async (dispatch: Function) => {
        UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.FETCHING_DATA);
        const services = new ServicesClientImpl();
        const onePassSharedPreferenceEditor = new SharedPreferences(
            SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY,
        ).edit();
        try {
            onePassSharedPreferenceEditor.putString(
                SharedPrefConstants.SELECTED_ONE_PASS_UCID,
                unifiedCollectionId,
            );
            onePassSharedPreferenceEditor.apply();
            const networkCallId = UniqueIdentifier.getNextOnePassServiceNetworkCallId();
            const onePassOptionsWithValue = await services.getValuesWithDisplayOptions(
                unifiedCollectionId,
                networkCallId,
                channelId,
                contentId,
            );
            const onePassOptions = await services.getOnePassOptions(
                unifiedCollectionId,
                networkCallId,
                channelId,
                contentId,
            );

            // operation cancelled by user or new request made
            if (onePassOptions.networkCallId !== UniqueIdentifier.getOnePassServiceNetworkCallId())
                return;

            const dynamicOptionsGenerator = new DynamicOptionsEngine();
            dynamicOptionsGenerator.setOnePassOptions(
                onePassOptions.onePassOptions,
                onePassOptionsWithValue.onePassValuesWithDisplayOptions.onePassValues!,
            );
            const initialOnePassOptionValues =
                dynamicOptionsGenerator.getInitialValuesOfOnePassOptions();
            const overlayOptions: TOverlayOptions[] = [];

            if (type === OnePassOptionsOverlayType.CREATE) {
                overlayOptions.push({
                    text: Locale.getString(LocaleKeys.OPO_CREATE_ONEPASS_WITH_THESE_OPTIONS),
                    value: OnePassOptionsOverlayActions.CREATE_ONE_PASS,
                } as TOverlayOptions);
                overlayOptions.push({
                    text: Locale.getString(LocaleKeys.OPO_CANCEL_DONT_SAVE_ONEPASS).replace(
                        `'`,
                        '\u2019',
                    ),
                    value: OnePassOptionsOverlayActions.EXIT_ONE_PASS_OVERLAY,
                } as TOverlayOptions);
            }

            if (type === OnePassOptionsOverlayType.MODIFY) {
                overlayOptions.push({
                    text: Locale.getString(LocaleKeys.OPO_USE_THESE_OPTIONS),
                    value: OnePassOptionsOverlayActions.UPDATE_ONE_PASS,
                } as TOverlayOptions);
                overlayOptions.push({
                    text: Locale.getString(LocaleKeys.OPO_DONT_CHANGE_OPTIONS).replace(
                        `'`,
                        '\u2019',
                    ),
                    value: OnePassOptionsOverlayActions.EXIT_ONE_PASS_OVERLAY,
                } as TOverlayOptions);
                overlayOptions.push({
                    text: Locale.getString(LocaleKeys.OPO_CANCEL_THIS_ONEPASS),
                    value: OnePassOptionsOverlayActions.DELETE_ONE_PASS,
                } as TOverlayOptions);
            }

            overlayOptions.push(...initialOnePassOptionValues);
            const overlayProps: IOverlayDialog = {
                enableCaret: true,
                hasDimmer: true,
                mouseTrapValue: GLOBAL_MOUSE_TRAP,
                options: overlayOptions,
                subtitle,
                title: Locale.getString(LocaleKeys.OPO_OVERLAY_TITLE),
                hasSeperator: false,
            };
            UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.DATA_LOADED);
            const showOnePassOverlay =
                !!onePassOptionsWithValue.onePassValuesWithDisplayOptions.alwaysShowOverlay;
            if (!showOnePassOverlay && type === OnePassOptionsOverlayType.CREATE) {
                Log.i(
                    TAG,
                    'showOnePassOptionsOverlay: not showing overlay as alwaysShowOverlay is false',
                    {
                        unifiedCollectionId,
                        channelId,
                        contentId,
                        alwaysShowOverlay: showOnePassOverlay,
                    },
                );
                Diagnostics.info(
                    TAG,
                    'showOnePassOptionsOverlay: not showing overlay as alwaysShowOverlay is false',
                    {
                        unifiedCollectionId,
                        channelId,
                        contentId,
                        alwaysShowOverlay: showOnePassOverlay,
                    },
                );
                const horizontalPositions = overlayOptions.map((option: TOverlayOptions) => {
                    return option.initialHorizontalIndex || 0;
                });
                dispatch(
                    checkStorageAndPerformOnePassAction(
                        overlayOptions,
                        horizontalPositions,
                        OnePassOptionsOverlayType.CREATE,
                    ),
                );
            } else {
                dispatch(showOverlayDialog(overlayProps));
                dispatch(
                    setScreenTaskController(
                        ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK,
                    ),
                );
            }
        } catch (error) {
            Log.e(TAG, `showOnePassOptionsOverlay`, { type }, { unifiedCollectionId }, error);
            clearOnePassSharedPreference();
            UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.DATA_LOADED);
            const whisperMessage = Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE);
            dispatch(showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }));
        }
    };

/**
 * key navigation action creators
 */
export const onKeyLeftOnePassOptionsOverlay = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, horizontalPosition, horizontalLimit } = getState().overlayHighlight;
    const { uiProperties } = getState().overlayDialog;
    const { options }: { options: TOverlayOptions[] } = uiProperties;
    if (horizontalLimit[verticalPosition] > 1) {
        const newHorizontalPosition =
            (horizontalPosition[verticalPosition] - 1 + horizontalLimit[verticalPosition]) %
            horizontalLimit[verticalPosition];
        const { updatedHorizontalPositions, newOptions } = computeNewOptions(
            options,
            verticalPosition,
            horizontalPosition,
            newHorizontalPosition,
        );
        dispatch(setOverlayHighlightHorizontalPositionAction(updatedHorizontalPositions));
        dispatch(setOverlayDialogOptions(newOptions));
    }
    dispatch(onKeyLeftOverlayDialog());
};

export const onKeyOkCancelOnePassOverlay = () => (dispatch: Function, getState: Function) => {
    const { overlayDialog, overlayHighlight } = getState();
    const { verticalPosition } = overlayHighlight;
    const { uiProperties } = overlayDialog;
    const { options } = uiProperties;
    const selectedOption = options[verticalPosition];
    Log.i(TAG, 'one pass selected: ', selectedOption);
    const onePassSharedPreferenceEditor = new SharedPreferences(
        SharedPrefConstants.ONE_PASS_ACTION_PREF_KEY,
    ).edit();
    onePassSharedPreferenceEditor.putString(
        SharedPrefConstants.SELECTED_ONE_PASS_UCID,
        selectedOption.value,
    );
    onePassSharedPreferenceEditor.apply();
    dispatch(hideOverlayDialog());
    dispatch(prepareDOCOverlay());
};

// onKeyOk Delete OnePass Confirmation Overlay
export const onKeyOkDOCOverlay = () => (dispatch: Function, getState: Function) => {
    const { overlayDialog, overlayHighlight } = getState();
    const { verticalPosition } = overlayHighlight;
    const { uiProperties } = overlayDialog;
    const { options } = uiProperties;
    const selectedOption = options[verticalPosition];
    switch (selectedOption.value) {
        case DOCOverlayActions.YES:
            dispatch(deleteOnepass());
            break;
        case DOCOverlayActions.NO:
            dispatch(hideOverlayDialog());
            clearOnePassSharedPreference();
            break;
        default:
    }
};
export const onKeyRightOnePassOptionsOverlay = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, horizontalPosition, horizontalLimit } = getState().overlayHighlight;
    const { uiProperties } = getState().overlayDialog;
    const { options }: { options: TOverlayOptions[] } = uiProperties;
    if (horizontalLimit[verticalPosition] > 1) {
        const updatedHorizontalPosition =
            (horizontalPosition[verticalPosition] + 1) % horizontalLimit[verticalPosition];
        const { updatedHorizontalPositions, newOptions } = computeNewOptions(
            options,
            verticalPosition,
            horizontalPosition,
            updatedHorizontalPosition,
        );
        dispatch(setOverlayHighlightHorizontalPositionAction(updatedHorizontalPositions));
        dispatch(setOverlayDialogOptions(newOptions));
    }
    dispatch(onKeyRightOverlayDialog());
};

export const onKeyOkOnePassOptionsOverlay = () => (dispatch: Function, getState: Function) => {
    const { overlayDialog, overlayHighlight } = getState();
    const { verticalPosition, horizontalPosition } = overlayHighlight;
    const { uiProperties } = overlayDialog;
    const { options } = uiProperties;
    const selectedOption = options[verticalPosition];
    switch (selectedOption.value) {
        case OnePassOptionsOverlayActions.CREATE_ONE_PASS:
            dispatch(
                checkStorageAndPerformOnePassAction(
                    options,
                    horizontalPosition,
                    OnePassOptionsOverlayType.CREATE,
                ),
            );
            break;
        case OnePassOptionsOverlayActions.DELETE_ONE_PASS:
            dispatch(prepareDOCOverlay());
            break;
        case OnePassOptionsOverlayActions.EXIT_ONE_PASS_OVERLAY:
            dispatch(hideOverlayDialog());
            clearOnePassSharedPreference();
            break;
        case OnePassOptionsOverlayActions.UPDATE_ONE_PASS:
            dispatch(
                checkStorageAndPerformOnePassAction(
                    options,
                    horizontalPosition,
                    OnePassOptionsOverlayType.MODIFY,
                ),
            );
            break;
        default:
            dispatch(setOverlayHighlightVerticalPositionAction(0));
            break;
    }
};

/**
 * mouse operations
 */
export const onMouseClickOnePassOptionsOverlay = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    if (value === GLOBAL_MOUSE_TRAP) {
        dispatch(hideOverlayDialog());
        new DynamicOptionsEngine().clearOnePassOptions();
        return;
    }
    if (value === InteractiveComponents.EAST) dispatch(onKeyRightOnePassOptionsOverlay());
    else if (value === InteractiveComponents.WEST) dispatch(onKeyLeftOnePassOptionsOverlay());
    else dispatch(onKeyOkOnePassOptionsOverlay());
};

export const onMouseClickDOCOptionsOverlay = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    if (value === GLOBAL_MOUSE_TRAP) {
        dispatch(hideOverlayDialog());
        new DynamicOptionsEngine().clearOnePassOptions();
    } else dispatch(onKeyOkDOCOverlay());
};
